import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize, Minimize, ZoomIn, ZoomOut, LayoutGrid, Search, BookOpen, Zap, WifiOff, X, Volume2, VolumeX } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { PageView } from "@/components/player/PageView";
import { MediaModal } from "@/components/player/MediaModal";
import { FlipBook } from "@/components/player/FlipBook";
import { useNetworkProfile, usePagePreloader, usePreloadThumbs } from "@/components/player/preload";
import { playFlipSound } from "@/lib/flipSound";

const Ctrl = ({ onClick, label, children, testid, active }) => (
  <button onClick={onClick} aria-label={label} title={label} data-testid={testid} className={`rounded-full p-2 transition-colors ${active ? "bg-[#3D3DF0] text-white text-white" : "text-[#334155] hover:bg-[#E2E8F3] hover:text-[#0F172A]"}`}>{children}</button>
);

const VARIANTS = {
  flip: {
    initial: (d) => ({ rotateY: d > 0 ? 70 : -70, opacity: 0.3, transformOrigin: d > 0 ? "left center" : "right center" }),
    animate: (d) => ({ rotateY: 0, opacity: 1, transformOrigin: d > 0 ? "left center" : "right center", transition: { duration: 0.32, ease: [0.2, 0.8, 0.2, 1] } }),
    exit: (d) => ({ rotateY: d > 0 ? -70 : 70, opacity: 0, transformOrigin: d > 0 ? "left center" : "right center", transition: { duration: 0.22 } }),
  },
  fast: {
    initial: (d) => ({ x: d * 40, opacity: 0 }),
    animate: { x: 0, opacity: 1, transition: { duration: 0.12 } },
    exit: { opacity: 0, transition: { duration: 0.06 } },
  },
};

const SidePanel = ({ title, onClose, children, testid }) => (
  <div className="absolute inset-y-0 left-0 z-30 flex w-72 flex-col bg-[#EEF2F8]/95 shadow-[12px_0_24px_-8px_rgba(166,180,208,.7)] backdrop-blur-xl" data-testid={testid}>
    <div className="flex items-center justify-between p-4"><span className="font-mono text-[11px] uppercase tracking-widest text-[#64748B]">{title}</span><button onClick={onClose} aria-label="Close panel" data-testid={`${testid}-close`}><X className="h-4 w-4" /></button></div>
    <div className="flex-1 overflow-y-auto px-4 pb-4 thin-scroll">{children}</div>
  </div>
);

export const BookPlayer = ({ manifest, variant = "read", fetchText, fetchLines, onQuiz, onEvent, onMetric, keyboard = true }) => {
  const { pages, layers } = manifest;
  const net = useNetworkProfile();
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [viewMode, setViewMode] = useState(manifest.default_mode || "flip");
  const [zoom, setZoom] = useState(1);
  const [panel, setPanel] = useState(null);
  const [modal, setModal] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showCtrl, setShowCtrl] = useState(true);
  const [isFs, setIsFs] = useState(false);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [query, setQuery] = useState("");
  const [texts, setTexts] = useState(null);
  const [linesMap, setLinesMap] = useState(null);
  const [sound, setSound] = useState(() => localStorage.getItem("ibs-flip-sound") !== "off");
  const soundRef = useRef(sound);
  soundRef.current = sound;
  const toggleSound = () => setSound((v) => { localStorage.setItem("ibs-flip-sound", v ? "off" : "on"); return !v; });
  const onFlipStart = useCallback(() => soundRef.current && playFlipSound(), []);
  const root = useRef(), stage = useRef(), audio = useRef(null), touch = useRef(null), navT = useRef(0), firstPaint = useRef(false), hideT = useRef(), flipRef = useRef();

  const page = pages[idx];
  const isFlip = viewMode === "flip";
  const fitPage = isFlip ? pages[0] : page;
  useEffect(() => {
    const fit = () => {
      const el = stage.current;
      if (!el || !fitPage) return;
      const spread = isFlip && el.clientWidth >= 1024;
      const aw = el.clientWidth - (window.innerWidth < 640 ? 16 : 140), ah = el.clientHeight - 48;
      const ratio = fitPage.width / fitPage.height;
      const w = Math.floor(Math.min(spread ? aw / 2 : aw, ah * ratio) * zoom);
      setSize((s) => (s.w === w && s.spread === spread ? s : { w, h: Math.round(w / ratio), spread }));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(stage.current);
    return () => ro.disconnect();
  }, [fitPage, zoom, isFlip]);

  usePreloadThumbs(pages);
  usePagePreloader(pages, idx, size.w, net);

  const onFlip = useCallback((i) => {
    setIdx((cur) => { setDir(i > cur ? 1 : -1); return i; });
  }, []);

  const go = useCallback((n) => {
    const t = Math.max(0, Math.min(pages.length - 1, n));
    if (isFlip && flipRef.current) {
      navT.current = performance.now();
      return flipRef.current.flip(t);
    }
    setIdx((cur) => {
      if (t === cur) return cur;
      setDir(t > cur ? 1 : -1);
      navT.current = performance.now();
      return t;
    });
  }, [pages.length, isFlip]);
  const next = () => (isFlip ? flipRef.current?.next() : go(idx + 1));
  const prev = () => (isFlip ? flipRef.current?.prev() : go(idx - 1));

  const stopAudio = () => { audio.current?.pause(); setPlayingAudio(null); };
  const playAudio = useCallback((layer) => {
    if (!layer.src) return;
    if (!audio.current) audio.current = new Audio();
    const a = audio.current;
    a.src = fileUrl(layer.src);
    a.loop = !!layer.loop;
    a.onended = () => setPlayingAudio(null);
    a.play().then(() => { setPlayingAudio(layer.id); onMetric?.("audio_start", performance.now()); }).catch(() => setPlayingAudio(null));
  }, [onMetric]);

  // Per-page lifecycle: stop previous audio, autoplay narration flagged for this page, record transition latency.
  useEffect(() => {
    stopAudio();
    visiblePages().forEach((p) => onEvent?.("page_view", { page: p }));
    const auto = (layers[pages[idx]?.index] || []).find((l) => l.type === "audio" && l.autoplay && !l.hidden);
    if (auto) playAudio(auto);
    if (navT.current) requestAnimationFrame(() => onMetric?.("transition", performance.now() - navT.current));
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const markFirstPaint = () => { if (!firstPaint.current) { firstPaint.current = true; onMetric?.("first_page", performance.now()); } };

  useEffect(() => {
    fetchLines?.().then((arr) => setLinesMap(Object.fromEntries(arr.map((d) => [d.index, d.lines])))).catch(() => {});
  }, [fetchLines]);

  const visiblePages = () => [pages[idx]?.index, ...(isFlip && size.spread && idx > 0 && pages[idx + 1] ? [pages[idx + 1].index] : [])].filter(Boolean);

  const onQuizComplete = onQuiz && ((layer, r) => {
    const pageIdx = Object.entries(layers).find(([, ls]) => ls.some((x) => x.id === layer.id))?.[0];
    return onQuiz({ layer_id: layer.id, title: layer.title || layer.name, page: Number(pageIdx) || 0, ...r });
  });

  const onAction = (l) => {
    const act = l.type === "hotspot" ? l.action : { audio: "audio", video: "video", image: "image", popup: "popup", quiz: "quiz", link: l.url && l.url !== "https://" ? "url" : "page" }[l.type];
    const lp = Number(Object.entries(layers).find(([, ls]) => ls.some((x) => x.id === l.id))?.[0]) || undefined;
    onEvent?.({ audio: "audio_play", video: "video_play", url: "link_click", page: "link_click" }[act] || "hotspot_tap", { page: lp, layer_id: l.id, layer_name: l.name });
    if (act === "audio") return playingAudio === l.id ? stopAudio() : playAudio(l);
    if (act === "url") return window.open(l.url, "_blank", "noopener");
    if (act === "page") return l.page && go(l.page - 1);
    setModal({ kind: act, layer: l });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!keyboard || e.target.tagName === "INPUT") return;
      if (e.key === "ArrowRight" || e.key === "PageDown") next();
      if (e.key === "ArrowLeft" || e.key === "PageUp") prev();
      if (e.key === "Home") go(0);
      if (e.key === "End") go(pages.length - 1);
      if (e.key === "Escape") setModal(null);
      if (e.key === "f") toggleFs();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggleFs = () => (document.fullscreenElement ? document.exitFullscreen() : root.current.requestFullscreen?.());

  const poke = () => {
    setShowCtrl(true);
    clearTimeout(hideT.current);
    hideT.current = setTimeout(() => setShowCtrl(false), 3500);
  };
  useEffect(() => { poke(); return () => clearTimeout(hideT.current); }, []);

  const openSearch = async () => {
    setPanel(panel === "search" ? null : "search");
    if (!texts) setTexts(await fetchText());
  };
  const results = query.trim().length > 1 && texts ? texts.filter((t) => t.text.toLowerCase().includes(query.toLowerCase())).slice(0, 50) : [];

  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current || zoom > 1 || isFlip) return;
    const dx = e.changedTouches[0].clientX - touch.current.x, dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) go(idx + (dx < 0 ? 1 : -1));
    touch.current = null;
  };

  const v = VARIANTS[viewMode];
  return (
    <div ref={root} onMouseMove={poke} onTouchStart={poke} className="relative flex h-full w-full select-none flex-col overflow-hidden bg-[#EEF2F8]" data-testid="book-player">
      <div ref={stage} className={`relative flex-1 thin-scroll ${zoom > 1 ? "overflow-auto" : "overflow-hidden"}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={{ perspective: 2200 }} data-testid="player-stage">
        <div className="flex min-h-full min-w-full items-center justify-center p-3" style={{ width: zoom > 1 ? size.w + 24 : undefined }}>
          {isFlip && size.w > 0 && (
            <FlipBook ref={flipRef} pages={pages} layers={layers} linesMap={linesMap} current={idx} size={size} spread={size.spread} lowBandwidth={net.lowBandwidth}
              onFlip={onFlip} onFlipStart={onFlipStart} onAction={onAction} playingAudio={playingAudio} onFirstPaint={markFirstPaint} />
          )}
          {!isFlip && <AnimatePresence initial={false} custom={dir} mode="popLayout">
            <motion.div key={idx} custom={dir} variants={v} initial="initial" animate="animate" exit="exit" style={{ transformStyle: "preserve-3d" }}>
              {page && size.w > 0 && (
                <PageView page={page} layers={layers[page.index]} lines={linesMap?.[page.index]} highlight={null} size={size} lowBandwidth={net.lowBandwidth} active onAction={onAction} playingAudio={playingAudio}
                  onFirstPaint={markFirstPaint} />
              )}
            </motion.div>
          </AnimatePresence>}
        </div>
        <button onClick={prev} disabled={idx === 0} aria-label="Previous page" data-testid="player-prev-btn" className={`absolute left-2 top-1/2 hidden -translate-y-1/2 neu-circle p-3 text-[#3D3DF0] transition-opacity disabled:opacity-0 sm:block ${showCtrl ? "opacity-100" : "opacity-0"}`}><ChevronLeft className="h-5 w-5" /></button>
        <button onClick={next} disabled={idx >= pages.length - (size.spread && idx > 0 ? 2 : 1)} aria-label="Next page" data-testid="player-next-btn" className={`absolute right-2 top-1/2 hidden -translate-y-1/2 neu-circle p-3 text-[#3D3DF0] transition-opacity disabled:opacity-0 sm:block ${showCtrl ? "opacity-100" : "opacity-0"}`}><ChevronRight className="h-5 w-5" /></button>
      </div>

      <div className={`pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center px-2 transition-[opacity,transform] duration-300 ${showCtrl || panel ? "opacity-100" : "translate-y-4 opacity-0"}`}>
        <div className="pointer-events-auto flex items-center gap-0.5 rounded-full neu-raised p-1.5" data-testid="player-controls">
          <Ctrl onClick={() => setPanel(panel === "thumbs" ? null : "thumbs")} label="Thumbnails" testid="player-thumbs-btn" active={panel === "thumbs"}><LayoutGrid className="h-4 w-4" /></Ctrl>
          <Ctrl onClick={openSearch} label="Search" testid="player-search-btn" active={panel === "search"}><Search className="h-4 w-4" /></Ctrl>
          <Ctrl onClick={prev} label="Previous page" testid="player-prev-ctrl"><ChevronLeft className="h-4 w-4" /></Ctrl>
          <span className="px-2 font-mono text-xs" data-testid="player-page-indicator">{isFlip && size.spread && idx > 0 && idx + 1 < pages.length ? `${idx + 1}–${idx + 2}` : idx + 1} / {pages.length}</span>
          <Ctrl onClick={next} label="Next page" testid="player-next-ctrl"><ChevronRight className="h-4 w-4" /></Ctrl>
          <Ctrl onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))} label="Zoom out" testid="player-zoom-out"><ZoomOut className="h-4 w-4" /></Ctrl>
          <span className="hidden w-10 text-center font-mono text-[11px] text-[#64748B] sm:inline" data-testid="player-zoom-level">{Math.round(zoom * 100)}%</span>
          <Ctrl onClick={() => setZoom((z) => Math.min(3, z + 0.5))} label="Zoom in" testid="player-zoom-in"><ZoomIn className="h-4 w-4" /></Ctrl>
          <Ctrl onClick={() => setViewMode(viewMode === "flip" ? "fast" : "flip")} label={viewMode === "flip" ? "Switch to Fast Reader" : "Switch to Classic Book"} testid="player-mode-toggle">{viewMode === "flip" ? <BookOpen className="h-4 w-4" /> : <Zap className="h-4 w-4 text-[#5FA82E]" />}</Ctrl>
          {isFlip && <Ctrl onClick={toggleSound} label={sound ? "Mute page-turn sound" : "Unmute page-turn sound"} testid="player-sound-toggle">{sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4 text-[#A62B8A]" />}</Ctrl>}
          <Ctrl onClick={toggleFs} label="Fullscreen" testid="player-fullscreen-btn">{isFs ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}</Ctrl>
          {net.lowBandwidth && <span className="px-2 text-[#64748B]" title="Low-bandwidth mode" data-testid="player-low-bandwidth"><WifiOff className="h-4 w-4" /></span>}
        </div>
      </div>

      {panel === "thumbs" && (
        <SidePanel title={`Pages · ${pages.length}`} onClose={() => setPanel(null)} testid="player-thumbs-panel">
          <div className="grid grid-cols-2 gap-3">
            {pages.map((p, i) => (
              <button key={p.index} onClick={() => { go(i); setPanel(null); }} data-testid={`player-thumb-${p.index}`} className={`rounded ring-2 ${i === idx ? "ring-[#3D3DF0]" : "ring-transparent hover:ring-white/30"}`}>
                <img src={fileUrl(p.thumb)} alt={`Page ${p.index}`} loading="lazy" className="w-full rounded bg-white" style={{ aspectRatio: `${p.width}/${p.height}` }} />
                <span className="font-mono text-[10px] text-[#64748B]">{p.index}</span>
              </button>
            ))}
          </div>
        </SidePanel>
      )}
      {panel === "search" && (
        <SidePanel title="Search book" onClose={() => setPanel(null)} testid="player-search-panel">
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search text…" className="mb-3 w-full rounded-xl border-0 neu-inset px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D3DF0]/40" data-testid="player-search-input" />
          {!texts && <p className="text-xs text-[#64748B]">Loading text layer…</p>}
          {query.trim().length > 1 && texts && results.length === 0 && <p className="text-xs text-[#64748B]" data-testid="player-search-empty">No matches</p>}
          {results.map((r) => {
            const at = r.text.toLowerCase().indexOf(query.toLowerCase());
            return (
              <button key={r.index} onClick={() => go(r.index - 1)} data-testid={`player-search-result-${r.index}`} className="mb-2 block w-full rounded-md p-2 text-left hover:bg-[#E2E8F3]">
                <span className="font-mono text-[10px] text-[#3D3DF0]">PAGE {r.index}</span>
                <p className="line-clamp-2 text-xs text-[#334155]">…{r.text.slice(Math.max(0, at - 40), at + 80)}…</p>
              </button>
            );
          })}
        </SidePanel>
      )}
      <MediaModal modal={modal} onClose={() => setModal(null)} onQuizComplete={onQuizComplete} />
    </div>
  );
};
